import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Plus, Loader2, StopCircle, ArrowLeft, Stethoscope, ArrowDown } from 'lucide-react';
import { ICON_PX } from '../ui/lib/iconSizes';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useChatScroll } from '../../hooks/useChatScroll';
import { ChatMessage } from './ChatMessage';
import { ChatSuggestions } from './ChatSuggestions';
import ReactMarkdown from 'react-markdown';
import { formatRelativeTime } from '../../utils/formatters';

export function ChatDrawer() {
  const {
    isOpen,
    activeSession,
    sessions,
    isStreaming,
    streamingText,
    streamError,
    pageContext,
    componentContext,
    closeChat,
    sendMessage,
    cancelStream,
    startNewSession,
    loadSession,
    submitFeedback,
  } = useChatbot();

  const [input, setInput] = useState('');
  const [showSessions, setShowSessions] = useState(false);
  const [sessionFilter, setSessionFilter] = useState('');
  const inputRef = useRef(null);

  const { containerRef, bottomRef, showScrollBtn, scrollToBottom } = useChatScroll({
    isStreaming,
    deps: [activeSession?.messages?.length, streamingText],
  });

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeChat();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeChat]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    sendMessage(text);
  };

  const handleSuggestionClick = (question) => {
    setInput('');
    sendMessage(question);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  const messages = activeSession?.messages ?? [];
  const hasMessages = messages.length > 0 || isStreaming;
  const suggestedQuestions = componentContext?.suggested ?? [];

  const contextLabel = componentContext?.componentId
    ? componentContext.componentId.replace(/\./g, ' > ').replace(/-/g, ' ')
    : pageContext?.pageName ?? '';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="fixed inset-0 bg-navy-deep/30 z-[89] md:hidden"
        onClick={closeChat}
      />

      {/* Drawer panel */}
      <div className="fixed right-0 top-0 h-full w-full md:top-[var(--header-height)] md:h-[calc(100vh-var(--header-height))] md:w-[400px] bg-nn-white border-l border-nn-border z-[90] flex flex-col shadow-lg">

        {/* ── Header ── */}
        <div className="shrink-0 px-4 py-3 border-b border-nn-border bg-surface-warm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={closeChat}
                className="md:hidden p-1 text-text-muted hover:text-text-dark"
              >
                <ArrowLeft size={ICON_PX.md} />
              </button>
              <Stethoscope size={ICON_PX.lg} className="text-royal shrink-0" />
              <span className="font-semibold text-body text-text-dark truncate">
                NexoNurse Assistant
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={closeChat}
                className="hidden md:flex p-1 text-text-muted hover:text-text-dark rounded transition-colors"
                title="Close"
              >
                <X size={ICON_PX.md} />
              </button>
            </div>
          </div>
          {contextLabel && (
            <p className="text-caption text-text-muted mt-1 truncate pl-7 md:pl-0">
              {contextLabel}
            </p>
          )}
        </div>

        {/* ── Messages area ── */}
        <div ref={containerRef} className="relative flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {!hasMessages && (
            <div className="space-y-4">
              {/* Welcome */}
              <div className="flex items-start gap-2">
                <div className="shrink-0 w-6 h-6 rounded-full bg-royal/20 flex items-center justify-center mt-0.5">
                  <Stethoscope size={ICON_PX.sm} className="text-royal" />
                </div>
                <div className="bg-slate-50 border border-nn-border rounded-lg px-3 py-2 text-body-sm text-text-dark">
                  <p>Welcome! I can help you understand any feature in NexoNurse. Ask me about metrics, navigation, or how things work.</p>
                </div>
              </div>

              {/* Suggested questions */}
              <ChatSuggestions
                questions={suggestedQuestions}
                onSelect={handleSuggestionClick}
              />
            </div>
          )}

          {/* Rendered messages */}
          {messages.map((msg, i) => (
            <ChatMessage
              key={`${msg.timestamp}-${i}`}
              message={msg}
              onFeedback={submitFeedback}
            />
          ))}

          {/* Streaming response — ReactMarkdown formats as text arrives */}
          {isStreaming && streamingText && (
            <div className="flex justify-start mb-3">
              <div className="max-w-[85%] flex items-start gap-2">
                <div className="shrink-0 w-6 h-6 rounded-full bg-royal/20 flex items-center justify-center mt-0.5">
                  <Stethoscope size={ICON_PX.sm} className="text-royal" />
                </div>
                <div className="bg-slate-50 border border-nn-border rounded-lg px-3 py-2 text-body-sm text-text-dark min-w-0">
                  <div className="prose prose-sm max-w-none [&>p]:mb-1.5 [&>p:last-child]:mb-0 [&>ul]:mb-1.5 [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:mb-1.5 [&>ol]:ml-4 [&>ol]:list-decimal [&>h3]:text-body [&>h3]:font-semibold [&>h3]:text-aqua [&>h3]:mt-3 [&>h3]:mb-1 [&>h3:first-child]:mt-0 [&_li]:mb-0.5">
                    <ReactMarkdown>{streamingText}</ReactMarkdown>
                  </div>
                  <span className="inline-block w-1.5 h-4 bg-royal/50 animate-pulse ml-0.5" />
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator (before first text arrives) */}
          {isStreaming && !streamingText && (
            <div className="flex justify-start mb-3">
              <div className="max-w-[85%] flex items-start gap-2">
                <div className="shrink-0 w-6 h-6 rounded-full bg-royal/20 flex items-center justify-center mt-0.5">
                  <Stethoscope size={ICON_PX.sm} className="text-royal" />
                </div>
                <div className="bg-slate-50 border border-nn-border rounded-lg px-3 py-2 text-body-sm text-text-muted flex items-center gap-2">
                  <Loader2 size={ICON_PX.sm} className="animate-spin" />
                  Searching knowledge base...
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {streamError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-body-sm text-red-critical mb-3">
              {streamError}
            </div>
          )}

          <div ref={bottomRef} />

          {/* Scroll-to-bottom button */}
          {showScrollBtn && (
            <button
              data-testid="scroll-to-bottom"
              onClick={scrollToBottom}
              className="sticky bottom-2 ml-auto flex items-center justify-center w-8 h-8 rounded-full
                         bg-aqua text-white shadow-md hover:bg-teal-dark transition-colors"
              title="Scroll to bottom"
            >
              <ArrowDown size={ICON_PX.sm} />
            </button>
          )}
        </div>

        {/* ── Footer / Input ── */}
        <div className="shrink-0 border-t border-nn-border px-4 py-3 bg-surface-warm">
          {/* Session controls */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => { setShowSessions(!showSessions); setSessionFilter(''); }}
              className="text-caption text-text-muted hover:text-text-dark transition-colors"
            >
              {sessions.length} session{sessions.length !== 1 ? 's' : ''}
            </button>
            <button
              onClick={() => { startNewSession(); setShowSessions(false); }}
              className="flex items-center gap-1 text-caption text-aqua hover:text-teal-dark transition-colors"
            >
              <Plus size={ICON_PX.xs} />
              New chat
            </button>
          </div>

          {/* Session picker dropdown */}
          {showSessions && sessions.length > 0 && (
            <div className="mb-2 max-h-48 border border-nn-border rounded bg-nn-white flex flex-col">
              <div className="shrink-0 px-2 py-1.5 border-b border-nn-border">
                <input
                  type="text"
                  value={sessionFilter}
                  onChange={(e) => setSessionFilter(e.target.value)}
                  placeholder="Search sessions..."
                  className="w-full text-body-sm px-2 py-1 rounded border border-nn-border
                             placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-aqua"
                />
              </div>
              <div className="overflow-y-auto">
                {sessions
                  .filter((s) => !sessionFilter || (s.title || '').toLowerCase().includes(sessionFilter.toLowerCase()))
                  .map((s) => (
                    <button
                      key={s.sessionId}
                      onClick={() => { loadSession(s.sessionId); setShowSessions(false); }}
                      className={`flex items-center justify-between w-full px-3 py-1.5 text-body-sm hover:bg-surface-warm transition-colors ${
                        activeSession?.id === s.sessionId ? 'bg-aqua/10 text-aqua' : 'text-text-dark'
                      }`}
                    >
                      <span className="truncate min-w-0">{s.title || 'New conversation'}</span>
                      <span className="shrink-0 text-caption text-text-muted ml-2">
                        {formatRelativeTime(s.updatedAt || s.createdAt)}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); if (showSessions) setShowSessions(false); }}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything..."
              rows={1}
              className="flex-1 min-w-0 resize-none rounded-lg border border-nn-border bg-nn-white
                         px-3 py-2 text-body-sm text-text-dark placeholder:text-text-muted
                         focus:outline-none focus:ring-1 focus:ring-aqua
                         max-h-24 overflow-y-auto"
            />
            {isStreaming ? (
              <button
                type="button"
                onClick={cancelStream}
                className="shrink-0 p-2 rounded-lg bg-red-50 text-red-critical hover:bg-red-100 transition-colors"
                title="Stop"
              >
                <StopCircle size={ICON_PX.md} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim()}
                className="shrink-0 p-2 rounded-lg bg-aqua text-white hover:bg-teal-dark
                           disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Send"
              >
                <Send size={ICON_PX.md} />
              </button>
            )}
          </form>
        </div>
      </div>
    </>
  );
}
